import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import BonusQualificationPanel from './BonusQualificationPanel';
import BonusCentralOffers from './BonusCentralOffers';
import { datumUndZeit } from '../lib/zeit';
import { serverError } from './bonusWindows';

const box = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16 };
const button = { border: 0, borderRadius: 8, padding: '10px 14px', background: '#6d28d9', color: '#fff', cursor: 'pointer' };
const field = { width: '100%', boxSizing: 'border-box', padding: 10, border: '1px solid #9ca3af', borderRadius: 8 };
const pendingKey = id => `voogo-bonus-central-pending:${id}`;
const permittedPaths = new Set(['/admin/bonus-central/switch', '/admin/bonus-central/qualification-reset','/admin/bonus-central/offers','/admin/bonus-central/offer-switch','/admin/bonus-central/offer-remove']);
// Read-only server check of the offer form; never stored as a pending write.
const previewOffer = body => api.post('/admin/bonus-central/offer-preview', body).then(response => response.data);

export default function BonusCentral() {
  const [data, setData] = useState(null), [error, setError] = useState(''), [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false), [pending, setPending] = useState(null);
  const [scope, setScope] = useState('driver'), [driver, setDriver] = useState(''), [reason, setReason] = useState('');
  const [qualificationVersion, setQualificationVersion] = useState(0);
  const request = useRef(null), inFlight = useRef(false), generation = useRef(0);
  const load = async () => {
    const token = ++generation.current;
    try {
      const response = await api.get('/admin/bonus-central');
      if (token !== generation.current) return;
      request.current = null;
      const saved = sessionStorage.getItem(pendingKey(response.data.account_id));
      let recovered = null;
      try { recovered = saved ? JSON.parse(saved) : null; }
      catch (_) { throw new Error('pending_request_unreadable'); }
      request.current = recovered && recovered.account_id === response.data.account_id
        && permittedPaths.has(recovered.path) && ['post','patch'].includes(recovered.method) ? recovered : null;
      setData(response.data); setError('');
      setPending(request.current);
    } catch (_) {
      if (token === generation.current) {
        setData(null);
        setError('Bonus konnte nicht geladen werden. Bitte erneut versuchen.');
      }
    }
  };
  useEffect(() => { load(); return () => { generation.current += 1; }; }, []);

  const execute = async (operation, { onFailure } = {}) => {
    if (inFlight.current || !data) return;
    inFlight.current = true; setBusy(true); setError(''); setSuccess('');
    try {
      const body = request.current || { ...operation, account_id: data.account_id, body: { ...operation.body,
        request_id: window.crypto.randomUUID(), expected_revision: data.revision } };
      sessionStorage.setItem(pendingKey(data.account_id), JSON.stringify(body));
      request.current = body; setPending(body);
      const response = await api[body.method](body.path, body.body);
      sessionStorage.removeItem(pendingKey(data.account_id)); request.current = null; setPending(null);
      setSuccess(body.path.endsWith('qualification-reset')
        ? `${response.data.driver_count} Fahrer: Annahme und Storno zählen ab jetzt neu. Keine automatische Bonusfreigabe.`
        : body.path.endsWith('offer-remove')
          ? (response.data?.removed === 'deleted' ? 'Angebot gelöscht.' : 'Angebot beendet und ausgeblendet. Verdiente Boni, Buchungszeilen und Historie bleiben erhalten.')
          : 'Änderung übernommen.');
      if (body.path.endsWith('qualification-reset')) setQualificationVersion(value => value + 1);
      setReason(''); await load();return true;
    } catch (failure) {
      const status = failure?.response?.status;
      if ([400,403,404,409].includes(status)) {
        sessionStorage.removeItem(pendingKey(data.account_id)); request.current = null; setPending(null);
      }
      const code = failure?.response?.data?.error, known = status === 400 ? serverError(code) : null;
      if (status === 400 || (status === 404 && code === 'bonus_central_partner_missing')) onFailure?.(code);
      setError(status === 409 ? 'Inzwischen geändert. Bitte neu laden und die Auswahl prüfen.'
        : status === 403 ? 'Für diese Änderung fehlt die Berechtigung.'
          : status === 404 ? 'Partner, Fahrer oder Angebot ist nicht mehr verfügbar. Bitte neu laden.'
          : status === 400 ? `${known ? known[1] : 'Bitte die Eingaben prüfen.'} Es wurde nichts übernommen.`
            : 'Änderung noch nicht bestätigt. Bitte denselben Vorgang erneut versuchen.');
      return false;
    } finally { inFlight.current = false; setBusy(false); }
  };
  const toggle = (target, enabled, partner_id = null) => execute({ method: 'patch', path: '/admin/bonus-central/switch', body: { scope: target, enabled, partner_id } });
  const locked = busy || !!pending;
  return <main style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(12px, 3vw, 24px)', color: '#111827' }}>
    <h1>Bonus</h1>
    <p>Neue VOOGO-Bonusangebote sind für Partner bestimmt. Die Fahrerqualifikation bestimmt, welche Fahrten zählen.</p>
    <p>Bestehende Partnerboni bleiben in den Einstellungen erreichbar. Neue Angebote werden von VOOGO an Partner gezahlt.</p>
    {error && <div role="alert" style={box}><p>{error}</p><button style={button} disabled={busy} onClick={load}>Neu laden</button></div>}
    {success && <p role="status">{success}</p>}
    {pending && <section aria-label="Unbestätigte Änderung" style={box}>
      <p>Für die letzte Änderung fehlt die Bestätigung. Erst diesen Vorgang abschließen.</p>
      <button style={button} disabled={busy} onClick={() => execute(pending)}>Erneut versuchen</button>
    </section>}
    {!data ? <p>Bonus wird geladen…</p> : <>
      <section style={box} aria-label="Bonus einschalten">
        <h2>VOOGO-Bonus {data.master_enabled ? 'eingeschaltet' : 'ausgeschaltet'}</h2>
        <p>Der Hauptschalter wählt alle Partner für neue VOOGO-Bonusangebote aus. Bestehende Partnerboni und verdiente Ansprüche bleiben unverändert.</p>
        <button style={button} disabled={locked || !data.can_manage_finance} onClick={() => toggle('master', !data.master_enabled)}>
          {data.master_enabled ? 'VOOGO-Bonus ausschalten' : 'VOOGO-Bonus einschalten'}
        </button>
        <p>VOOGO-Woche: {datumUndZeit(data.current_week_start)} bis {datumUndZeit(data.next_week_start)}. Neue Woche, neuer Fortschritt; eingeschaltete Angebote bleiben eingeschaltet.</p>
      </section>
      <BonusCentralOffers data={data} locked={locked} onOperation={execute} onPreview={previewOffer} />
      <section style={box} aria-label="Partnerauswahl">
        <h2>Partner</h2>
        <p>Eine Auswahl für alle Partner ersetzt frühere Einzelentscheidungen. Der Hauptschalter hat Vorrang.</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <button style={button} disabled={locked || !data.can_manage_finance} onClick={() => toggle('all_partners', true)}>Alle Partner einschalten</button>
          <button style={button} disabled={locked || !data.can_manage_finance} onClick={() => toggle('all_partners', false)}>Alle Partner ausschalten</button>
        </div>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,260px),1fr))' }}>
          {data.partners.map(partner => <article key={partner.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
            <strong>{partner.company_name || partner.id}</strong>
            <p>{partner.effective ? 'Wirksam eingeschaltet' : !partner.master_enabled ? 'Global ausgeschaltet' : 'Für Partner ausgeschaltet'}</p>
            <button style={button} disabled={locked || !data.can_manage_finance} onClick={() => toggle('partner', !partner.partner_enabled, partner.id)}>
              {partner.partner_enabled ? 'Partner ausschalten' : 'Partner einschalten'}
            </button>
          </article>)}
        </div>
      </section>
      <section style={box} aria-label="Fahrerqualifikation neu beginnen">
        <h2>Qualifikation neu zählen</h2>
        <p>Frühere Annahmen und Stornos zählen danach nicht mehr für die neue Prüfung. Ereignisse und verdiente Boni bleiben erhalten.</p>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,240px),1fr))' }}>
          <label>Umfang<select style={field} value={scope} disabled={locked} onChange={e => setScope(e.target.value)}>
            <option value="driver">Ein Fahrer</option><option value="all">Alle Fahrer ({data.drivers.length})</option>
          </select></label>
          {scope === 'driver' && <label>Fahrer<select style={field} value={driver} disabled={locked} onChange={e => setDriver(e.target.value)}>
            <option value="">Bitte auswählen</option>{data.drivers.map(item => <option key={item.id} value={item.id}>{item.name || item.id}</option>)}
          </select></label>}
          <label>Grund<input style={field} maxLength={500} value={reason} disabled={locked} onChange={e => setReason(e.target.value)} /></label>
        </div>
        <p>Der Reset gewährt keinen Bonus. Erst neue, passende Beobachtungen können die Qualifikation erfüllen.</p>
        <button style={button} disabled={locked || !data.can_reset || !reason.trim() || (scope === 'driver' && !driver)}
          onClick={() => execute({ method: 'post', path: '/admin/bonus-central/qualification-reset', body: { scope, driver_id: scope === 'driver' ? driver : null, reason: reason.trim() } })}>
          {scope === 'all' ? 'Für alle Fahrer neu zählen' : 'Für diesen Fahrer neu zählen'}
        </button>
        {!!data.recent_resets.length && <details style={{ marginTop: 16 }}><summary>Letzte Resets</summary>
          {data.recent_resets.map(item => <p key={item.id}>{datumUndZeit(item.created_at_ms)} · {item.driver_count} Fahrer · {item.reason}</p>)}
        </details>}
      </section>
      <BonusQualificationPanel key={qualificationVersion} showCampaigns={false} canManage={data.can_reset && !locked} />
    </>}
  </main>;
}
